import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  Select,
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../components/layout/PageLayout';
import promoService from '../../services/promoService';
import { useUser } from '../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const PromoCampaignList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} campaigns`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: null
  });

  const getCampaigns = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        ...filters,
        ...params
      };
      
      console.log('Fetching campaigns with params:', requestParams);
      const response = await promoService.getAllCampaigns(requestParams);
      console.log('Campaigns API Response:', response);
      
      if (response.data && response.data.status) {
        const campaignsData = response.data.data || [];
        
        console.log('Campaigns data:', campaignsData);
        
        // Ensure campaignsData is an array
        if (Array.isArray(campaignsData)) {
          setCampaigns(campaignsData);
          setPagination(prev => ({
            ...prev,
            total: campaignsData.length
          }));
        } else {
          console.error('Campaigns data is not an array:', campaignsData);
          setCampaigns([]);
        }
      } else {
        message.error('Failed to fetch campaigns');
        setCampaigns([]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      message.error('Failed to fetch campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, filters.status]);

  // Load campaigns data
  useEffect(() => {
    getCampaigns();
  }, [getCampaigns]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getCampaigns({
      search: value,
      status: filters.status
    });
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getCampaigns({
      search: filters.search,
      status: value
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
  };

  // Handle add campaign
  const handleAddCampaign = () => {
    navigate('/promo/promo-campaign-form/new');
  };

  // Handle edit campaign
  const handleEditCampaign = (pcId) => {
    navigate(`/promo/promo-campaign-form/${pcId}`);
  };

  // Format date
  const getDateString = (val) => {
    if (val) {
      const dateStr = new Date(val).toLocaleDateString();
      return dateStr;
    } else {
      return 'No Date';
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'pc_name',
      key: 'pc_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.pc_name.localeCompare(b.pc_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Start Date',
      dataIndex: 'start',
      key: 'start',
      width: 120,
      render: (text) => (
        <Text style={{ fontSize: '13px' }}>
          {getDateString(text)}
        </Text>
      )
    },
    {
      title: 'Expiration',
      dataIndex: 'expiration',
      key: 'expiration',
      width: 120,
      render: (text) => (
        <Text style={{ fontSize: '13px' }}>
          {getDateString(text)}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditCampaign(record.pc_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Promo Campaigns</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Promo Campaign List"
      desc="Manage promo campaigns and their status"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Campaigns ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCampaign}
              size="large"
            >
              Add
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Select
              value={filters.status}
              onChange={handleStatusChange}
              placeholder="Status"
              style={{ width: 150 }}
              allowClear
            >
              <Option value={null}>All</Option>
              <Option value="Y">Active</Option>
              <Option value="N">Inactive</Option>
            </Select>
            
            <Search
              placeholder="Search campaigns..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={campaigns}
          rowKey="pc_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{ 
            emptyText: 'No campaigns found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default PromoCampaignList;